/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import { camelCase } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import useGameState from '../../hooks/useGameState';

import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import SimonButton from '../buttons/SimonButton';
import SimonCenterButtonIcon from '../icons/SimonCenterButton';
import SimonCenterButtonIconCoors from '../icons/SimonCenterButtonCoors';
import SparkleIcon from '../icons/Sparkle';
import StarIcon from '../icons/Star';
import BaseGame, { GameApi } from './Base';

import styles from '../../../styles/games/simon.module.css';

interface SimonGameProps {
    className?: string | null;
    game: SimonGame;
    participation: Participation;
    canPlay?: boolean;
    reload: () => void;
}

function SimonGame({
    game,
    participation,
    canPlay = false,
    reload,
    className = null,
}: SimonGameProps) {
    const { name: theme } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const { questions = null, choices = null } = game || {};

    const { answers: userAnswers = [] } = participation || {};

    const { currentQuestionIndex, currentQuestion, allAnswersValid } = useGameState(
        game,
        participation,
    );

    const [pendingAnswer, setPendingAnswer] = useState<string[] | null>(null);

    const { id: questionId = null, explanation: questionExplanation = null } =
        currentQuestion || {};

    const apiRef = useRef<GameApi>(null);
    const [questionLevel = '1'] = (questionId || '').split('_');
    const currentLevel = parseInt(questionLevel, 10);

    const [hasPlayed, setHasPlayed] = useState(false);

    const [choicesSequence, setChoicesSequence] = useState<any[] | null>(null);
    const [playingChoice, setPlayingChoice] = useState<any | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    useEffect(() => {
        if (choicesSequence === null) {
            return () => {};
        }
        const [firstChoice, ...newSequence] = choicesSequence;
        setPlayingChoice(firstChoice);
        audioRef.current.src = firstChoice.audio.url;
        audioRef.current.load();
        // audioRef.current.playbackRate = 4.0;
        audioRef.current.play();
        audioRef.current.onended = () => {
            setChoicesSequence(newSequence.length > 0 ? newSequence : null);
            setPlayingChoice(null);
        };
        return () => {
            audioRef.current.pause();
        };
    }, [choicesSequence]);

    const onClickPlay = useCallback(() => {
        if (choicesSequence !== null) {
            return;
        }

        const [firstChoice = null, ...newSequence] = (questionExplanation || '')
            .split('_')
            .map((it) => choices.find(({ id: choiceId }) => it === choiceId));

        if (firstChoice === null) {
            console.log('no first choice!', questionExplanation);
            return;
        }

        if (audioRef.current === null) {
            audioRef.current = document.createElement('audio');
            // audioRef.current.playbackRate = 2.0;
        } else {
            audioRef.current.pause();
        }

        setHasPlayed(true);
        setPlayingChoice(firstChoice);
        audioRef.current.src = firstChoice.audio.url;
        audioRef.current.load();
        // audioRef.current.playbackRate = 2.0;
        audioRef.current.play();
        audioRef.current.onended = () => {
            setChoicesSequence(newSequence.length > 0 ? newSequence : null);
            setPlayingChoice(null);
        };
    }, [questionExplanation]);

    const onClickChoice = useCallback(
        (e, choice) => {
            const { answerQuestion } = apiRef.current;
            const { id: choiceId, audio = null } = choice || {};
            const { url: audioUrl = null } = audio || {};
            const audioObj = document.createElement('audio');
            audioObj.src = audioUrl;
            audioObj.play();
            const newPendingAnswer = [...(pendingAnswer || []), choiceId];

            if (newPendingAnswer.length === currentLevel) {
                answerQuestion(questionId, newPendingAnswer.join('_'));
                setPendingAnswer(null);
            } else {
                setPendingAnswer(newPendingAnswer);
            }
        },
        [currentLevel, pendingAnswer, questionId],
    );

    const playActive = playingChoice !== null;

    const CenterButton = theme === 'coorslite' ? SimonCenterButtonIconCoors : SimonCenterButtonIcon;

    return (
        <BaseGame
            game={game}
            participation={participation}
            canPlay={canPlay}
            isValid={allAnswersValid}
            apiRef={apiRef}
            reload={reload}
            withFeedback
            statusBar={
                <div className={styles.level}>
                    <FormattedMessage defaultMessage="Level" description="Game level" />
                    <div className={styles.count}>
                        {currentQuestionIndex + 1}/{questions.length}
                    </div>
                </div>
            }
            className={classNames([styles.container, themeClassName, className])}
        >
            <div className={styles.game}>
                <div className={styles.buttons}>
                    <Button
                        className={classNames([
                            styles.centerButton,
                            {
                                [styles.down]: playActive,
                            },
                        ])}
                        onClick={onClickPlay}
                        disabled={playActive}
                    >
                        <CenterButton
                            down={playActive}
                            className={styles.icon}
                            backgroundColor={theme === 'coorslive' ? '#0070B9' : null}
                            shadowColor={theme === 'coorslive' ? '#030F2D' : null}
                        />
                        <div className={styles.playLabel}>
                            {theme === 'coorslive' ? (
                                <FormattedMessage
                                    defaultMessage="Press to start"
                                    description="Play coorslive"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Play"
                                    description="Button level"
                                />
                            )}
                        </div>
                    </Button>
                    {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map(
                        (position, index) => {
                            const {
                                id: choiceId,
                                color,
                                shadowColor,
                                downColor,
                                downShadowColor,
                                image: choiceImage,
                            } = choices[index] || {};

                            return (
                                <div
                                    key={position}
                                    className={classNames([
                                        styles.cell,
                                        styles[camelCase(position)],
                                    ])}
                                >
                                    <SimonButton
                                        key={`button-${position}`}
                                        position={position}
                                        image={choiceImage}
                                        color={color}
                                        shadowColor={shadowColor}
                                        downColor={downColor}
                                        downShadowColor={downShadowColor}
                                        down={
                                            playingChoice !== null && playingChoice.id === choiceId
                                        }
                                        className={classNames([
                                            styles.button,
                                            {
                                                [styles.withoutShadow]: shadowColor === null,
                                            },
                                        ])}
                                        disabled={!hasPlayed}
                                        onClick={(e) => onClickChoice(e, choices[index])}
                                    />
                                </div>
                            );
                        },
                    )}
                </div>
                <ul className={styles.progress}>
                    {questions.map(({ id }) => (
                        <li
                            key={id}
                            className={classNames([
                                styles.step,
                                {
                                    [styles.current]: id === currentQuestion.id,
                                    [styles.done]:
                                        (userAnswers || []).findIndex(
                                            ({ question }) => question === id,
                                        ) !== -1,
                                },
                            ])}
                        >
                            {theme === 'iga_noel' ? (
                                <SparkleIcon className={styles.icon} />
                            ) : (
                                <StarIcon className={styles.icon} />
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </BaseGame>
    );
}

export default SimonGame;
